import type {
  CareOptionType,
  RecipientType,
} from "~care/components/ui/LeadForm/CareSeekerShallowLeadForm/form-options";
import type {
  CareOption,
  CareOptionSelection,
} from "../forms/care-seeker-lead-form";
import type {
  ShallowCareOption,
  ShallowCareOptionSelection,
} from "../forms/care-seeker-shallow-lead-form";

import { EventName } from "@homeinstead/shared";

type AnalyticsEvent = {
  event: string;
};

type FormSubmissionResult =
  | "success"
  | "duplicate"
  | "server-error"
  | "validation-error";

type SvcLeadsSubmitAnalyticsEvent = AnalyticsEvent & {
  franchise_number?: string;
  form_id: string;
  form_submission_result: FormSubmissionResult;
  form_submission_intent: string;
  page_url: string;
  lang: string;
  selected_options?: CareOptionType[] | undefined;
  recipient?: RecipientType | undefined;
};

type CareerFormSubmitAnalyticsEvent = AnalyticsEvent & {
  franchise_number?: string;
  form_id: string;
  form_submission_result: FormSubmissionResult;
  form_submission_intent: "career";
  page_url: string;
  lang: string;
};

type FranchiseOpportunitiesFormSubmitEvent = AnalyticsEvent & {
  page_url: string;
};

type FranchiseOpportunitiesFormStepEvent = AnalyticsEvent & {
  page_url: string;
  step: number;
};

type ConfidentialApplicationFormSubmitEvent = AnalyticsEvent & {
  page_url: string;
};

type ConfidentialApplicationFormStepEvent = AnalyticsEvent & {
  page_url: string;
  step: number;
};

// The tag manager trigger, triggers on
//  element_name === <service_types_next|employment_btn|service_recipient>
type ElementName =
  | "service_types_next"
  | "employment_btn"
  | "service_recipient"
  | "service_location_btn";

type TrackedClickEvent = AnalyticsEvent & {
  element_name: ElementName;
  page_zone: string;
  page_url: string;
  page_path: string;
  lang: string;
};

type ServiceTypeTrackedClickEvent = TrackedClickEvent & {
  element_name: "service_types_next";
  selected_options: Array<CareOption | ShallowCareOption>;
  IsCorporate: boolean;
};

type ServiceLocationTrackedClickEvent = TrackedClickEvent & {
  element_name: "service_location_btn";
  PostalCode: number;
  OfficeLocation: number;
  IsCorporate: boolean;
};

type ServiceRecipientTrackedClickEvent = TrackedClickEvent & {
  element_name: "service_recipient";
  recipient: string;
  IsCorporate: boolean;
};

type EmployButtonTrackedClickEvent = TrackedClickEvent & {
  element_name: "employment_btn";
};

type CTAButtonInteractionEvent = AnalyticsEvent & {
  page_url: string;
  lang: string;
};

type URLParamsEvent = AnalyticsEvent & {
  params: Record<string, string>;
};

export const TrackedClickEvent = <T extends ElementName>({
  element_name,
  page_path,
  page_url,
}: {
  element_name: T;
  page_path: string;
  page_url: string;
}): TrackedClickEvent & { element_name: T } => ({
  element_name: element_name,
  event: "tracked_click",
  lang: "en",
  page_path: page_path,
  page_url: page_url,
  page_zone: "ppc",
});

/**
 * @deprecated Please use ServiceTypeTrackedClickEvent from @homeinstead/analytics instead
 */
export const ServiceTypeTrackedClickEvent = (
  selected_options: CareOptionSelection | ShallowCareOptionSelection,
  page_path: string,
  page_url: string,
): ServiceTypeTrackedClickEvent => ({
  ...TrackedClickEvent({
    element_name: "service_types_next",
    page_path,
    page_url,
  }),
  IsCorporate: false,
  selected_options,
});

/**
 * @deprecated Please use ServiceLocationTrackedClickEvent from @homeinstead/analytics instead
 */
export const ServiceLocationTrackedClickEvent = (
  PostalCode: number,
  OfficeLocation: number,
  page_path: string,
  page_url: string,
): ServiceLocationTrackedClickEvent => ({
  ...TrackedClickEvent({
    element_name: "service_location_btn",
    page_path,
    page_url,
  }),
  IsCorporate: false,
  OfficeLocation,
  PostalCode,
});

/**
 * @deprecated Please use EmploymentButtonTrackedClickEvent from @homeinstead/analytics instead
 */
export const EmployButtonTrackedClickEvent = (
  page_path: string,
  page_url: string,
): EmployButtonTrackedClickEvent => ({
  ...TrackedClickEvent({
    element_name: "employment_btn",
    page_path,
    page_url,
  }),
});

/**
 * @deprecated Please use ServiceRecipientTrackedClickEvent from @homeinstead/analytics instead
 */
export const ServiceRecipientTrackedClickEvent = (
  recipient: string,
  page_path: string,
  page_url: string,
): ServiceRecipientTrackedClickEvent => ({
  recipient: recipient,
  ...TrackedClickEvent({
    element_name: "service_recipient",
    page_path,
    page_url,
  }),
  IsCorporate: false,
});

export const CTAButtonInteractionEvent = (): CTAButtonInteractionEvent => ({
  event: "cta-btn-interaction",
  lang: "en",
  page_url: window.location.href,
});

/**
 * @deprecated Please use ServiceLeadFormSubmitEvent from @homeinstead/analytics instead
 */
export const ServiceLeadFormSubmitEvent = ({
  franchiseNumber,
  result,
  page_url,
  selectedOptions,
  recipient,
}: {
  franchiseNumber?: string | undefined;
  result: FormSubmissionResult;
  page_url: string;
  selectedOptions?: CareOptionType[] | undefined;
  recipient?: RecipientType | undefined;
}): SvcLeadsSubmitAnalyticsEvent => ({
  event: EventName.SERVICE_LEAD_FORM_SUBMIT,
  form_id: "service-lead-form-ppc-restructure",
  form_submission_intent: "service",
  form_submission_result: result,
  franchise_number: franchiseNumber,
  lang: "en",
  page_url: page_url,
  recipient,
  selected_options: selectedOptions,
});

export const CareerFormSubmitEvent = ({
  franchiseNumber,
  result,
  page_url,
}: {
  franchiseNumber?: string | undefined;
  result: FormSubmissionResult;
  page_url: string;
}): CareerFormSubmitAnalyticsEvent => ({
  event: "career-form-submit",
  form_id: "career-inquiry-form-ppc-restructure",
  form_submission_intent: "career",
  form_submission_result: result,
  franchise_number: franchiseNumber,
  lang: "en",
  page_url: page_url,
});

export const FranchiseOpportunitiesFormSubmitEvent = ({
  page_url,
}: {
  page_url: string;
}): FranchiseOpportunitiesFormSubmitEvent => ({
  event: "request-information-form-submit",
  page_url,
});

export const FranchiseOpportunitiesFormStepEvent = ({
  page_url,
  step,
}: {
  page_url: string;
  step: number;
}): FranchiseOpportunitiesFormStepEvent => ({
  event: "request-information-form-step",
  page_url,
  step,
});

export const ConfidentialApplicationFormSubmitEvent = ({
  page_url,
}: {
  page_url: string;
}): ConfidentialApplicationFormSubmitEvent => ({
  event: "caf-form-submit",
  page_url,
});

export const ConfidentialApplicationFormStepEvent = ({
  page_url,
  step,
}: {
  page_url: string;
  step: number;
}): ConfidentialApplicationFormStepEvent => ({
  event: "caf-form-step",
  page_url,
  step,
});

export const URLParamsEvent = ({
  params,
}: {
  params: Record<string, string>;
}): Record<string, string> => ({
  ...params,
});
